// CSV building and download helpers (SFB-1309).
//
// Replaces the ad-hoc `data:text/csv` exports: those did not quote commas or
// quotes (a name like `Perez, Juan` shifted every column), broke on `#` and
// long payloads, and showed accents wrongly in Excel for lack of a BOM.

const UTF8_BOM = '﻿';

// Cells that start with these are evaluated as formulas by Excel/Sheets, so a
// member named `=HYPERLINK(...)` would run on the admin's machine. Prefixing a
// single quote makes the spreadsheet treat the cell as text.
const FORMULA_TRIGGER = /^[=+\-@\t\r]/;

/**
 * Serializes one cell: null/undefined become empty, formula triggers are
 * neutralized, and anything with a delimiter, quote or line break is quoted.
 * Numbers are left alone so negative values (e.g. net profit) stay numeric.
 */
export function escapeCsvCell(value) {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '';
  let text = String(value);
  if (FORMULA_TRIGGER.test(text)) text = `'${text}`;
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/**
 * @param {string[]} headers
 * @param {Array<Array<*>>} rows each row aligned with `headers`
 * @returns {string} CSV text (CRLF line endings, as Excel expects), no BOM
 */
export function buildCsv(headers, rows) {
  return [headers, ...rows]
    .map((row) => row.map(escapeCsvCell).join(','))
    .join('\r\n');
}

/**
 * Triggers a browser download of `csv` as `filename`. Uses a Blob (no size
 * limit like data: URIs) and prepends the BOM so Excel reads it as UTF-8.
 */
export function downloadCsv(filename, csv) {
  const blob = new Blob([UTF8_BOM, csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename.endsWith('.csv') ? filename : `${filename}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/** Convenience: build and download in one call. */
export function exportCsv(filename, headers, rows) {
  downloadCsv(filename, buildCsv(headers, rows));
}
